import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { format } from "date-fns";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { CheckoutContext } from "v3/context/checkout";
import { TenantContext } from "v3/context/tenant";
import { PaymentContext } from "v3/context/payment";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { LocalizationContext } from "v3/context/localization";
import { getSortedTermsByLocation } from "@pepdirect/v3/ui/TermsAndConditions";
import { ReviewSection } from "v3/components/checkout/ReviewSection";
import { DEFAULT_REQUESTED_DELIVERY_DATE } from "v3/components/checkout/RequestedDeliveryDate";
import {
  useFinalizePriceMutation,
  useSaveShippingOptionsMutation,
  useShopToCheckoutMutation,
  useSubmitOrderMutation,
} from "v3/services/graphql/generated";
import { getPaymentDetailsForReviewStep } from "v3/helpers/payment";
import { getTimeZoneFromBrowser } from "v3/helpers/localization";
import { CHECKOUT_STEPS } from "v3/types/checkout";
import {
  logPurchaseComplete,
  logPurchaseEmailOptIn,
} from "v3/services/analytics";
import { useCurrentAppId } from "v3/hooks/useCurrentAppId";

interface ReviewStepProps {
  editPayment: (shouldNotClearErrors?: boolean) => void;
  editShip: () => void;
}

/**
 * THIS IS FOR STRIPE ONLY!!
 */
export const StripeReviewStep: FC<ReviewStepProps> = ({
  editPayment,
  editShip,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saveRequestedDeliveryDate] = useSaveShippingOptionsMutation();
  const [submitOrder] = useSubmitOrderMutation();
  const [shopToCheckout] = useShopToCheckoutMutation();
  const [finalizePrice] = useFinalizePriceMutation();

  const {
    apiBaseUrl,
    checkoutSession,
    cartId,
    setIsLoading,
    transitionToStep,
  } = useContext(CheckoutContext);
  const { tenant } = useContext(TenantContext);
  const { appId } = useCurrentAppId();

  const {
    availablePaymentMethods,
    clearPaymentDataOnEdit,
    generatePaymentIntentSecret,
    paymentNonce,
    paymentType,
    saveCardForLater,
    savePaymentInput,
    stripeTokenDetails,
  } = useContext(PaymentContext);
  const { clearAllErrors, handleCheckoutErrors, setErrorMsg } =
    useContext(CheckoutErrorContext);
  const { genericErrorMsg, localization } = useContext(LocalizationContext);

  const {
    paymentFailureErrorText = "There was an error processing your payment. Please try again or choose a new payment method to continue.",
  } = localization?.checkout?.errors || {};

  const {
    contactInfo,
    id,
    payment,
    requestedDeliveryDate,
    hasGasExchangeItems,
  } = checkoutSession || {};
  const { shippingAddress } = contactInfo || {};
  const { billingAddress } = payment || {};

  const [finalRequestedDeliveryDate, setFinalRequestedDeliveryDate] =
    useState<string>(requestedDeliveryDate || "");

  const showRequestedDelivery =
    tenant?.userExperience?.hasRequestedDeliveryDate;

  const callShopToCheckoutForOrderStatus = async () => {
    try {
      const { data } = await shopToCheckout({
        variables: {
          cartId,
        },
      });
      if (data?.shopToCheckout?.payment?.lastErrorCode) {
        // calling finalize price because shopToCheckout resets taxes
        await finalizePrice({
          variables: {
            id,
          },
        });
        await generatePaymentIntentSecret(id);
        transitionToStep(CHECKOUT_STEPS.payment);
        setErrorMsg(paymentFailureErrorText);
      }
    } catch (err) {
      handleCheckoutErrors(err);
    }
  };
  // if skipToReview, handle undefined delivery date if tenant has requested delivery date enabled
  useEffect(() => {
    const handleRequestedDeliveryDate = async () => {
      if (showRequestedDelivery && !finalRequestedDeliveryDate) {
        try {
          const { data } = await saveRequestedDeliveryDate({
            variables: {
              id,
              input: {
                requestedDeliveryDate: format(
                  DEFAULT_REQUESTED_DELIVERY_DATE,
                  "yyyy-MM-dd"
                ),
              },
            },
          });
          if (data?.saveShippingOptions?.requestedDeliveryDate) {
            setFinalRequestedDeliveryDate(
              data.saveShippingOptions.requestedDeliveryDate
            );
          }
        } catch (e) {
          handleCheckoutErrors(e);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    handleRequestedDeliveryDate();
  }, [
    finalRequestedDeliveryDate,
    handleCheckoutErrors,
    id,
    saveRequestedDeliveryDate,
    setIsLoading,
    showRequestedDelivery,
  ]);

  const paymentDetails = useMemo(() => {
    return getPaymentDetailsForReviewStep({
      availablePaymentMethods,
      billingAddress,
      paymentType,
      savedToken: checkoutSession?.payment?.token,
      stripeTokenDetails,
    });
  }, [
    availablePaymentMethods,
    billingAddress,
    checkoutSession,
    paymentType,
    stripeTokenDetails,
  ]);

  const handleEditPayment = (shouldNotClearErrors?: boolean) => {
    clearPaymentDataOnEdit();
    editPayment(shouldNotClearErrors);
  };

  const sortedTerms = useMemo(() => {
    return getSortedTermsByLocation({
      location: "review",
      terms: tenant?.brandConfig.customTexts || [],
      hasGasExchangeItems,
    });
  }, [hasGasExchangeItems, tenant]);

  const validateTerms = useCallback(
    (values: Record<string, boolean>) => {
      const errors: Record<string, string> = {};
      sortedTerms?.forEach((term) => {
        if (!values[term.type]) {
          errors[term.type] = `${term.type} is required`;
        }
      });
      return errors;
    },
    [sortedTerms]
  );

  const handlePlaceOrder = async () => {
    clearAllErrors();
    setIsLoading(true);

    try {
      const { data } = await submitOrder({
        variables: {
          id,
          input: {
            paymentNonce,
            preferredTimezone: getTimeZoneFromBrowser(),
          },
        },
      });
      const { submitOrder: submittedOrder } = data || {};

      if (submittedOrder?.id) {
        logPurchaseComplete(
          checkoutSession?.cart,
          appId,
          apiBaseUrl,
          submittedOrder
        );
        const { contactInfo } = submittedOrder;
        const emailForOptIn = !!contactInfo?.emailOptIn && contactInfo.email;
        if (emailForOptIn) logPurchaseEmailOptIn(emailForOptIn);
      }
    } catch (e) {
      handleCheckoutErrors(e);
      setIsLoading(false);
      return;
    }

    if (savePaymentInput?.savedCreditCard) {
      await callShopToCheckoutForOrderStatus();
      return;
    }

    // This is from a Stripe error on the FE.
    // Since we are in `payment` stage now after calling submitOrder,
    // we must validate session after a stripe error to let BE know it failed too
    let stripeError = "";

    if (stripe && elements) {
      const fullName = `${shippingAddress?.firstName || ""} ${
        shippingAddress?.lastName || ""
      }`.trim();

      try {
        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: location.href,
            ...(saveCardForLater
              ? { save_payment_method: saveCardForLater }
              : {}),
            payment_method_data: {
              billing_details: {
                name: fullName,
                phone: contactInfo?.phone || undefined,
                address: {
                  city: shippingAddress?.city || undefined,
                  country: shippingAddress?.countryCode || undefined,
                  line1: shippingAddress?.line1 || undefined,
                  line2: shippingAddress?.line2 || undefined,
                  postal_code: shippingAddress?.zip || undefined,
                  state: shippingAddress?.state || undefined,
                },
              },
            },
          },
          redirect: "if_required",
        });
        if (error) {
          stripeError = paymentFailureErrorText;
        }
      } catch (e) {
        stripeError = genericErrorMsg;
        setErrorMsg(stripeError);
        setIsLoading(false);
        return;
      }
    }

    await callShopToCheckoutForOrderStatus();
    return;
  };

  // something went wrong
  if (!paymentDetails) return null;

  return (
    <ReviewSection
      // terms must be checked for SST even when logged in
      // currently SST is the only tenant using Stripe and therefore this component
      // so this is a workaround to always have this value be true. When this
      // changes later on, we will need to extend tenant.brandConfig.legal
      // to indicate this so that this assumption is not made on the FE.
      alwaysRequireCheckboxes
      editPayment={handleEditPayment}
      editShip={editShip}
      onSubmit={handlePlaceOrder}
      paymentDetails={paymentDetails}
      terms={sortedTerms}
      validateTerms={validateTerms}
    />
  );
};
